import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCarBurst,
  faMask,
  faCloudBolt,
  faQuestion,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { StepProps, PhysicalDamageSubcategory } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const SectionLabel = styled.div`
  ${tw`text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3`}
`;

const OptionsContainer = styled.div`
  ${tw`flex flex-col gap-3 mb-8`}
`;

const OptionCard = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-4 text-left border-2 rounded-card transition-all flex items-center gap-4`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const IconWrapper = styled.div<{ isSelected: boolean }>`
  ${tw`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-pink-100` : tw`bg-bg-secondary`)}
`;

const Icon = styled(FontAwesomeIcon)<{ isSelected: boolean }>`
  ${props => (props.isSelected ? tw`text-black` : tw`text-text-secondary`)}
`;

const CardContent = styled.div`
  ${tw`flex-1`}
`;

const CardTitle = styled.h3`
  ${tw`font-semibold text-text-primary mb-1`}
`;

const CardDescription = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const CheckIcon = styled.div<{ isSelected: boolean }>`
  ${tw`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-black` : tw`bg-bg-secondary`)}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const subcategoryOptions = [
  {
    value: PhysicalDamageSubcategory.COLLISION,
    icon: faCarBurst,
    title: 'Collision',
    description: 'Damage caused by hitting another vehicle, curb, pole, or other object.',
  },
  {
    value: PhysicalDamageSubcategory.THEFT_VANDALISM,
    icon: faMask,
    title: 'Theft or vandalism',
    description: 'Damage caused by someone stealing from or vandalizing the vehicle.',
  },
  {
    value: PhysicalDamageSubcategory.WEATHER_NATURAL_DISASTER,
    icon: faCloudBolt,
    title: 'Weather or natural disaster',
    description: 'Damage caused by floods, hail storms, earthquakes, or other natural events.',
  },
  {
    value: PhysicalDamageSubcategory.SOMETHING_ELSE,
    icon: faQuestion,
    title: 'Something else',
    description: "Damage caused by something other than what's listed above.",
  },
];

const SubcategoryStep: React.FC<StepProps> = ({ formData, updateFormData, onNext }) => {
  const handleSelect = (subcategory: PhysicalDamageSubcategory) => {
    updateFormData({ physicalDamageSubcategory: subcategory });
  };

  const handleContinue = () => {
    if (formData.physicalDamageSubcategory) {
      onNext();
    }
  };

  return (
    <Container>
      <SectionLabel>DAMAGE DETAILS</SectionLabel>

      <OptionsContainer>
        {subcategoryOptions.map(option => (
          <OptionCard
            key={option.value}
            isSelected={formData.physicalDamageSubcategory === option.value}
            onClick={() => handleSelect(option.value)}
          >
            <IconWrapper isSelected={formData.physicalDamageSubcategory === option.value}>
              <Icon
                icon={option.icon}
                isSelected={formData.physicalDamageSubcategory === option.value}
              />
            </IconWrapper>
            <CardContent>
              <CardTitle>{option.title}</CardTitle>
              <CardDescription>{option.description}</CardDescription>
            </CardContent>
            <CheckIcon isSelected={formData.physicalDamageSubcategory === option.value}>
              {formData.physicalDamageSubcategory === option.value && (
                <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
              )}
            </CheckIcon>
          </OptionCard>
        ))}
      </OptionsContainer>

      <ContinueButton disabled={!formData.physicalDamageSubcategory} onClick={handleContinue}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default SubcategoryStep;
